// src/utils/useCurrency.js
import { useState, useEffect, useMemo } from "react";
import { useAuthContext } from "../context/auth";
import { loadExchangeRates, getDolarFallback, getDolarLastDate, getUFFallback, isLoaded } from "./exchangeRateService";

export const useCurrency = () => {
  const { pais } = useAuthContext();
  const normalizedPais = pais ? pais.charAt(0).toUpperCase() + pais.slice(1).toLowerCase() : "";

  const [loaded, setLoaded] = useState(isLoaded());

  useEffect(() => {
    if (isLoaded()) {
      setLoaded(true);
      return;
    }
    loadExchangeRates().then(() => setLoaded(true));
  }, []);

  const usdToLocal = useMemo(() => (loaded ? getDolarFallback(normalizedPais) : null), [loaded, normalizedPais]);
  const ufValue = useMemo(
    () => (loaded && normalizedPais === "Chile" ? getUFFallback() : null),
    [loaded, normalizedPais]
  );

  // ====================
  // 1. MONEDAS DISPONIBLES Y DEFAULTS
  // ====================
  const monedasDisponibles = useMemo(
    () => (normalizedPais === "Chile" ? ["UF", "USD", "CLP"] : ["USD", "PEN"]),
    [normalizedPais]
  );

  const getDefaultMoneda = (tipo = "gestion") => {
    if (normalizedPais === "Chile") {
      return tipo === "hardware" ? "CLP" : "UF";
    }
    return "PEN";
  };

  // ====================
  // 2. CONVERTIDOR UNIVERSAL
  // ====================
  const convert = (amount, fromMoneda, toMoneda) => {
    if (!amount) return 0;
    const value = parseFloat(amount);

    if (normalizedPais === "Chile") {
      if (fromMoneda === toMoneda) return value;

      let inCLP = value;

      if (fromMoneda === "UF" && ufValue) inCLP = value * ufValue;
      if (fromMoneda === "USD" && usdToLocal) inCLP = value * usdToLocal;
      if (fromMoneda === "CLP") inCLP = value;

      if (toMoneda === "CLP") return inCLP;
      if (toMoneda === "UF" && ufValue) return inCLP / ufValue;
      if (toMoneda === "USD" && usdToLocal) return inCLP / usdToLocal;
    } else if (normalizedPais === "Perú" && usdToLocal) {
      if (fromMoneda === "USD" && toMoneda === "PEN") return value * usdToLocal;
      if (fromMoneda === "PEN" && toMoneda === "USD") return value / usdToLocal;
    }

    return value;
  };

  // ====================
  // 3. FORMATEADOR
  // ====================
  const formatAmount = (amount, moneda = "CLP", customOptions = {}) => {
    if (amount == null || isNaN(amount)) return "-";
    const value = parseFloat(amount);
    const locale = normalizedPais === "Chile" ? "es-CL" : "es-PE";
    const options = {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      ...customOptions,
    };
    if (moneda === "UF") {
      options.minimumFractionDigits = 2;
      options.maximumFractionDigits = 2;
    }
    if (moneda === "USD" || moneda === "PEN") {
      options.minimumFractionDigits = 0;
      options.maximumFractionDigits = 0;
    }
    const formattedNumber = value.toLocaleString(locale, options);
    const prefixMap = {
      UF: "UF ",
      USD: "US$ ",
      CLP: "$ ",
      PEN: "S/ ",
    };
    const prefix = prefixMap[moneda] || "";
    return prefix + formattedNumber;
  };

  return {
    monedasDisponibles,
    getDefaultMoneda,
    usdToLocal,
    ufValue,
    loadingRates: !loaded,
    exchangeDate: loaded ? getDolarLastDate(normalizedPais) : null,
    convert,
    formatAmount,
  };
};
